import type { FC, PropsWithChildren } from "react"
import { createContext, useContext, useEffect } from "react"

import { useAuth } from "@/contexts/auth"
import useDomain from "@/hooks/use-domain"
import useWhitelabelConfiguration from "@/hooks/use-whitelabel-configuration"

const WhitelabelContext = createContext<
  WhitelabelContextProps | undefined
>(undefined)

export const WhitelabelContextProvider: FC<
  PropsWithChildren
> = ({ children }) => {
  const { authData } = useAuth()
  const domain = useDomain()
  const merchantId = authData?.user?.merchantId

  const params = merchantId ? { merchantId } : { domain }

  const { data: configuration, refetch } =
    useWhitelabelConfiguration(params)

  useEffect(() => {
    if (configuration?.visualPreferences?.layout?.colors) {
      const { colors } =
        configuration.visualPreferences.layout
      const root = document.documentElement

      if (colors.topLineBackground) {
        root.style.setProperty(
          "--whitelabel-primary",
          colors.topLineBackground
        )
      }
      if (colors.background) {
        root.style.setProperty(
          "--whitelabel-background",
          colors.background
        )
      }
      if (colors.buttonConfirmBackground) {
        root.style.setProperty(
          "--whitelabel-button-bg",
          colors.buttonConfirmBackground
        )
      }
      if (colors.buttonConfirmBackgroundHover) {
        root.style.setProperty(
          "--whitelabel-button-bg-hover",
          colors.buttonConfirmBackgroundHover
        )
      }
      if (colors.buttonConfirmText) {
        root.style.setProperty(
          "--whitelabel-button-text",
          colors.buttonConfirmText
        )
      }
    }
  }, [configuration])

  const value: WhitelabelContextProps = {
    configuration: configuration || null,
    isLoading: false,
    error: null,
    refetch: async () => {
      await refetch()
    },
  }

  return (
    <WhitelabelContext.Provider value={value}>
      {children}
    </WhitelabelContext.Provider>
  )
}

export const useWhitelabel = (): WhitelabelContextProps => {
  const context = useContext(WhitelabelContext)
  if (!context) {
    throw new Error(
      "useWhitelabel must be used within WhitelabelContextProvider"
    )
  }
  return context
}
